import { useSelector, useDispatch } from "react-redux";
import { Grid, Row, Col, Button, Text, FlexboxGrid, Divider } from "rsuite";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import IconIndicator from "../user-feedback/IconIndicator";
import Notice from "../user-feedback/Notice";
import { selectConfigExpressRegistrationRenewEnabled, selectConfigExpressRegistrationTypeVerbiage } from "../../store/slices/config/configSelectors";
import { getPermitActionabilityAsync } from "../../store/slices/licenseView/licenseViewThunks";
import { selectPermitActionability, selectPermitActionabilityInProgress, selectReportingHistory, selectReportingHistoryInProgress, selectReportingHistorySuccess } from "../../store/slices/licenseView/licenseViewSelectors";
import { getAddressWithUnitNumber, isNullUndefinedOrEmptyString, titleize } from "../../common/utils";
import usePlaceNavigate from "../../hooks/usePlaceNavigate";
import { initiateUpdateFlow } from "../../store/slices/update/updateThunks";
import { selectInitiatingUpdateFlow, selectInitiatingUpdateFlowError } from "../../store/slices/update/updateSelectors";
import { initiateRenewFlow } from "../../store/slices/renew/renewThunks";
import { selectInitiatingRenewFlow, selectInitiatingRenewFlowError } from "../../store/slices/renew/renewSelectors";
import { C_RENEWAL_STATUS_SUBMITTED } from "../../common/consts";

const Header = ({ applicationNumber }) => {
  const dispatch = useDispatch();
  const placeNavigate = usePlaceNavigate();

  const initiatingUpdateFlow = useSelector(selectInitiatingUpdateFlow);
  const initiatingUpdateFlowError = useSelector(selectInitiatingUpdateFlowError);

  const [showInitiateUpdateFlowError, setShowInitiateUpdateFlowError] = useState(false);
  const typeVerbiage = useSelector(selectConfigExpressRegistrationTypeVerbiage);
  const renewEnabled = useSelector(selectConfigExpressRegistrationRenewEnabled);

  const permitActionablity = useSelector(selectPermitActionability);
  const permitActionablityLoading = useSelector(selectPermitActionabilityInProgress);

  const reportingHistory = useSelector(selectReportingHistory);
  const reportingHistoryLoading = useSelector(selectReportingHistoryInProgress);
  const reportingHistorySuccess = useSelector(selectReportingHistorySuccess);

  const [showInitiateRenewFlowError, setShowInitiateRenewFlowError] = useState(false);
  const initiatingRenewFlow = useSelector(selectInitiatingRenewFlow);
  const initiatingRenewFlowError = useSelector(selectInitiatingRenewFlowError);

  useEffect(() => {
    dispatch(getPermitActionabilityAsync(applicationNumber));
  }, [dispatch]);

  useEffect(() => {
    if (initiatingUpdateFlow) {
      setShowInitiateUpdateFlowError(false);
    }
    else {
      if (isNullUndefinedOrEmptyString(initiatingUpdateFlowError)) {
        setShowInitiateUpdateFlowError(false);
      }
      else {
        setShowInitiateUpdateFlowError(true);
        setTimeout(() => {
          setShowInitiateUpdateFlowError(false);
        }, 10000); // 10 seconds 
      }
    }
  }, [initiatingUpdateFlow, initiatingUpdateFlowError]);

  useEffect(() => {
    if (initiatingUpdateFlow) {
      setShowInitiateRenewFlowError(false);
    }
    else {
      if (isNullUndefinedOrEmptyString(initiatingRenewFlowError)) {
        setShowInitiateRenewFlowError(false);
      }
      else {
        setShowInitiateRenewFlowError(true);
        setTimeout(() => {
          setShowInitiateRenewFlowError(false);
        }, 10000); // 10 seconds 
      }
    }
  }, [initiatingRenewFlow, initiatingRenewFlowError]);

  const address = useMemo(() => {
    const propertyAddress = reportingHistory?.propertyInfo?.propertyAddress;
    if (!isNullUndefinedOrEmptyString(propertyAddress)) {
      return getAddressWithUnitNumber(propertyAddress, reportingHistory?.propertyInfo?.unitNumber);
    }
    else {
      return "-"
    }
  }, [reportingHistory]);

  const renewalSubmitted = useMemo(() => {
    return reportingHistory?.renewals?.some(renewalApp => renewalApp.status === C_RENEWAL_STATUS_SUBMITTED) ?? false;
  }, [reportingHistory]);

  const isPermitApplication = useMemo(() => {
    return !reportingHistory?.certificateInfo?.licenseNumber;
  }, [reportingHistory]);

  const dateLabel = useMemo(() => {
    return isPermitApplication ? "Application Date" : "Expiration Date"
  }, [isPermitApplication]);

  const date = useMemo(() => {
    return isPermitApplication ? reportingHistory?.certificateInfo?.applicationDate :
      reportingHistory?.certificateInfo?.expirationDate ?? '-';
  }, [reportingHistory]);

  const statusLabel = useMemo(() => {
    return `${typeVerbiage} Status`
  }, [typeVerbiage]);

  const status = useMemo(() => {
    return titleize(reportingHistory?.status ?? '-');
  }, [reportingHistory]);

  const licenseNumberLabel = useMemo(() => {
    return isPermitApplication ? "Application Number" : `${typeVerbiage} Number`
  }, [reportingHistory, isPermitApplication, typeVerbiage]);

  const licenseNumber = useMemo(() => {
    return isPermitApplication ? reportingHistory?.certificateInfo?.applicationNumber :
      reportingHistory?.certificateInfo?.licenseNumber;
  }, [reportingHistory, isPermitApplication]);

  const canUpdate = useMemo(() => {
    return permitActionablity?.updatable;
  }, [permitActionablity]);

  const canRenew = useMemo(() => {
    return permitActionablity?.renewable;
  }, [permitActionablity, renewEnabled]);

  const hasAction = useMemo(() => {
    return canUpdate || canRenew;
  }, [canUpdate, canRenew]);

  const isLoading = useMemo(() => {
    return permitActionablityLoading || reportingHistoryLoading;
  }, [permitActionablityLoading, reportingHistoryLoading])

  const onFlowStepsMapped = (path) => {
    placeNavigate(`/${path}`);
  }

  const initiateUpdateFlowClick = useCallback((_e) => {
    if (!initiatingUpdateFlow) {
      dispatch(initiateUpdateFlow(applicationNumber, `/single-permit/${applicationNumber}`, onFlowStepsMapped));
    }
  }, [applicationNumber, reportingHistory, initiatingUpdateFlow]);

  const initiateRenewFlowClick = useCallback(() => {
    if (!initiatingRenewFlow) {
      dispatch(initiateRenewFlow(applicationNumber, `/single-permit/${applicationNumber}`, onFlowStepsMapped));
    }
  }, [applicationNumber, reportingHistory, initiatingRenewFlow]);

  return (
    isLoading ? (
      <IconIndicator className='mt-20' />
    ) :
      <>
        {
          !reportingHistorySuccess && (
            <Grid fluid className='mt-20'>
              <Row>
                <Col xs={24}>
                  <Notice message='Something went wrong while fetching license information.' />
                </Col>
              </Row>
            </Grid>
          )
        }
        {
          reportingHistorySuccess && (
            <Grid fluid className='mt-20'>
              <Row>
                <Col xs={24}>
                  <Text className="header-text-h3 text-bold">{address}</Text>
                </Col>
              </Row>
            </Grid>
          )
        }
        <Grid fluid className='mt-24 content-section'>
          <FlexboxGrid as={Row} align="middle">
            {
              reportingHistorySuccess && (
                <FlexboxGrid.Item as={Col} xs={24} md={hasAction ? 18 : 24}>
                  <Grid fluid>
                    <FlexboxGrid as={Row} style={{ display: 'flex', alignItems: 'stretch' }}>
                      <FlexboxGrid.Item as={Col} xs={12} md={5} className="mb-16">
                        <Text align="center" className="text-bold mb-4 text-large ">{date}</Text>
                        <Text className="grey-text" align="center">{dateLabel}</Text>
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item as={Col} xs={1} xsHidden smHidden mdHidden={false} className="flex-row-md">
                        <Divider vertical className="permit-attribute-seperator" />
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item as={Col} xs={12} md={5} className="mb-16">
                        <Text align="center" className="text-bold mb-4 text-large ">{status} {renewalSubmitted ? "/ Renewal Submitted" : ""}</Text>
                        <Text className="grey-text" align="center">{statusLabel}</Text>
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item as={Col} xs={1} xsHidden smHidden mdHidden={false} className="flex-row-md">
                        <Divider vertical className="permit-attribute-seperator" />
                      </FlexboxGrid.Item>
                      <FlexboxGrid.Item as={Col} xs={12} md={5} className="mb-16">
                        <Text align="center" className="text-bold mb-4 text-large ">{licenseNumber}</Text>
                        <Text className="grey-text" align="center">{licenseNumberLabel}</Text>
                      </FlexboxGrid.Item>
                    </FlexboxGrid>
                  </Grid>
                </FlexboxGrid.Item>
              )
            }
            {
              hasAction && (
                <FlexboxGrid.Item as={Col} xs={24} md={6} lg={4} lgPush={2}>
                  <div role="grid">
                    <Row>
                      {
                        canUpdate && (
                          <Col xs={24} sm={12} md={24} className="mb-16" style={{ textAlign: "right" }}>
                            <Button
                              className="btn-primary"
                              style={{ width: '100%' }}
                              onClick={(e) => { initiateUpdateFlowClick(e); }}
                              disabled={initiatingUpdateFlow}
                            >
                              Update {typeVerbiage}
                              {
                                initiatingUpdateFlow && (
                                  <IconIndicator color="white" className="ph-8" />
                                )
                              }
                            </Button>
                          </Col>
                        )
                      }
                      {
                        canRenew && (
                          <Col xs={24} sm={12} md={24} style={{ textAlign: "right" }}>
                            <Button className="btn-primary" style={{ width: '100%' }} onClick={() => { initiateRenewFlowClick(); }}>
                              Renew {typeVerbiage}
                              {
                                initiatingRenewFlow && (
                                  <IconIndicator color="white" className="ph-8" />
                                )
                              }
                            </Button>
                          </Col>
                        )
                      }
                    </Row>
                  </div>
                </FlexboxGrid.Item>
              )
            }
            {
              showInitiateUpdateFlowError && (
                <FlexboxGrid.Item as={Col} xs={24}>
                  <Notice style={{ marginTop: '10px' }} message={initiatingUpdateFlowError} />
                </FlexboxGrid.Item>
              )
            }

            {
              showInitiateRenewFlowError && (
                <FlexboxGrid.Item as={Col} xs={24}>
                  <Notice style={{ marginTop: '10px' }} message={initiatingRenewFlowError} />
                </FlexboxGrid.Item>
              )
            }
          </FlexboxGrid>
        </Grid>
      </>
  );
};

export default Header; 
